import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../api";
import CloseProjectDialog from "../components/CloseProjectDialog";
import { hasAtLeast, isGlobalAdmin, rankInGroup, ROLE_RANK } from "../auth/roles";
import { useAuth } from "../auth/AuthContext";
import { PortalLayout } from "../components/Portal";
import { G, G_BG, G_DARK, Badge, StatusBadge, Btn, Spinner, ConfirmDialog,
         Field, Textarea, formatWhen } from "../components/Shared";
import { fmtBytes } from "../wizard/SubmissionWizard";
import { useDisplayTimezone } from "../portalConfig";
import { useLiveRefresh } from "../liveRefresh";
// The three tabs, and what a submission and a delivery have in common. Split
// out on 2026-08-25 — this file was 2 097 lines and a question about one tab
// loaded all three.
import OverviewTab, { ProjectLogCard } from "./OverviewTab";
import SubmissionsTab from "./SubmissionsTab";
import ResultsTab from "./ResultsTab";
import { ARCHIVE_LOOK, HEALTH_LOOK } from "./projectRecords";

export default function ProjectDetailPage() {
  const zone = useDisplayTimezone();
  const { id }    = useParams();
  const navigate  = useNavigate();
  const { user }  = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [tab, setTab]         = useState("overview");
  const [closing, setClosing] = useState(null);   // "finish" | "cancel" | null
  // The reason for taking a project back out of the archive, while the dialog
  // is open. Required by the server since 2026-09-14 and never asked for here,
  // so Reopen answered with the server's own sentence about saying why and gave
  // nowhere to say it.
  const [reopening, setReopening] = useState(null);   // { reason, busy } | null

  useEffect(() => {
    api.get(`/api/projects/${id}`)
      .then(p => setProject(p))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  // Standing in THIS project's group, mirroring the backend's _manages_project.
  // `role === "operator"` read the cache of the strongest grant, so a group
  // admin — who outranks an operator — failed it and lost the Verify buttons,
  // the "Add from S3" form, the Notify button and the S3 configuration card.
  const canManage = isGlobalAdmin(user)
    || (rankInGroup(user, project?.group_id) !== null
        && rankInGroup(user, project?.group_id) <= ROLE_RANK.operator);

  // Pulls the project back down after a tab changes something that lives on it:
  // the storage-health fields behind the banner, and the submission/delivery
  // counts on the tab badges. The server has already re-run the health check by
  // the time this is called, so it only has to read the answer.
  const refreshProject = useCallback(async () => {
    try { setProject(await api.get(`/api/projects/${id}`)); } catch { /* keep what we have */ }
  }, [id]);
  // And after any change made anywhere on the page, or on coming back to the
  // tab: the net under onProjectChanged, which a new mutation can forget to
  // call — the submission count did, 2026-10-07. See liveRefresh.js.
  useLiveRefresh(refreshProject);

  // The status list is still published on /auth/config and still read by the
  // filter on the projects list — this page just no longer offers a free
  // choice of status, so it does not keep one.
  // How long a transfer has, for the notices on the Submissions/Results tabs.
  // Taken from the /auth/config call that was already being made rather than a
  // second request, and left null when the backend doesn't send it so the
  // notice renders nothing instead of "undefined hours".
  const [sessionHours, setSessionHours] = useState(null);
  useEffect(() => {
    api.get("/auth/config").then(c => {
      if (c.session_hours) setSessionHours(c.session_hours);
    }).catch(() => {});
  }, []);
  const [deleteInfo, setDeleteInfo] = useState(null);   // deletion-preview payload while the dialog is open
  const [deleting, setDeleting]     = useState(false);

  // Opening the dialog fetches real counts and the S3 prefix, so the
  // confirmation can state what is removed and what is left behind rather than
  // a generic "this cannot be undone".
  const openDelete = async () => {
    setDeleteInfo({ loading: true });
    try {
      setDeleteInfo(await api.get(`/api/projects/${id}/deletion-preview`));
    } catch (err) {
      setDeleteInfo(null);
      setError(err.message);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/api/projects/${id}`);
      navigate("/projects");
    } catch (err) {
      setDeleteInfo(null);
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return (
    <PortalLayout>
      <div style={{ textAlign: "center", padding: "80px 0", color: "#6b7280" }}>Loading project…</div>
    </PortalLayout>
  );

  if (error || !project) return (
    <PortalLayout>
      <div style={{ textAlign: "center", padding: "80px 0" }}>
        <div style={{ color: "#dc2626", fontSize: 14, marginBottom: 16 }}>{error || "Project not found"}</div>
        <Btn variant="secondary" onClick={() => navigate("/projects")}>← Back to Projects</Btn>
      </div>
    </PortalLayout>
  );

  if (project.status === "archived" && !hasAtLeast(user, "operator")) {
    return (
      <PortalLayout>
        <div style={{ textAlign: "center", padding: "80px 0" }}>
          <div style={{ fontSize: 32, marginBottom: 16 }}>🗄️</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#374151", marginBottom: 8 }}>Project not available</div>
          <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 24 }}>This project has been archived.</div>
          <Btn variant="secondary" onClick={() => navigate("/projects")}>← Back to Projects</Btn>
        </div>
      </PortalLayout>
    );
  }

  const tabs = ["overview", "submissions", "results"];
  const tabLabel = { overview: "Overview", submissions: "Submissions", results: "Results" };

  return (
    <PortalLayout>
      {/* On a phone the last item is one ellipsised line (data-breadcrumb in
          globalStyles): an unbroken name here took the page to 482px, and the
          title below says it in full. The title attribute keeps it readable. */}
      <div data-breadcrumb style={{ marginBottom: 20, fontSize: 13, color: "#6b7280" }}>
        <Link to="/projects" style={{ color: "#6b7280", textDecoration: "none" }}>Projects</Link>
        <span style={{ margin: "0 8px" }}>›</span>
        <span data-breadcrumb-current title={project.name} style={{ color: "#1a1a1a", fontWeight: 600 }}>{project.name}</span>
      </div>

      {/* data-page-header/data-page-actions are the hooks the 768px breakpoint
          already uses on Jobs and Management: the row stacks and the action
          block loses its margin-left:auto. This page never carried them, so on
          a phone the title, the status control, the badges and Delete stayed on
          one unwrapping line and the auto margin kept shoving Delete to the far
          right of an overflowing row.

          The metadata is wrapped in one element on purpose — the rule stacks
          *children*, and six full-width blocks is not a header. Two children,
          same shape as the other pages. */}
      <div data-page-header style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 28 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", minWidth: 0 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, overflowWrap: "anywhere" }}>{project.name}</h1>
        {/* The status dropdown lived here and is gone. Every project starts
            active and only Finish or Cancel moves it on — both of which archive
            it and record why — so offering the three states as a free choice
            was a second, unrecorded way to do the same thing, and the one that
            left no reason behind.

            Reopening survives as its own button because it is the repair for
            pressing Finish on the wrong project, and the server has always
            allowed it to an admin alone. */}
        <StatusBadge status={project.status} />
        {project.status === "archived" && user?.is_admin && (
          <button
            onClick={() => { setError(null); setReopening({ reason: "", busy: false }); }}
            style={{ fontSize: 12, padding: "3px 10px", borderRadius: 6, border: "1px solid #9ca3af",
                     background: "#fff", color: "#374151", cursor: "pointer", fontFamily: "inherit", minHeight: 24 }}
          >
            Reopen
          </button>
        )}
        {/* Why it is archived, not just that it is. Three quite different
            outcomes used to be one word, and the reason is what somebody asks
            about a year later. */}
        {project.status === "archived" && project.archive_reason && (
          <span
            title={project.archive_note || undefined}
            style={{ fontSize: 12, padding: "2px 9px", borderRadius: 10, fontWeight: 600,
                     background: ARCHIVE_LOOK[project.archive_reason]?.bg || "#f3f4f6",
                     color: ARCHIVE_LOOK[project.archive_reason]?.fg || "#374151" }}
          >
            {ARCHIVE_LOOK[project.archive_reason]?.label || project.archive_reason}
          </span>
        )}
        {/* A project that has been reopened three times must not present itself
            as untouched. */}
        {project.reopened_count > 0 && (
          <span style={{ fontSize: 12, color: "#6b7280" }}
                title="An admin took this project back out of the archive.">
            reopened {project.reopened_count}×
          </span>
        )}
        {/* Only the owner finishes or cancels (2026-11-03): the server's
            `is_owner`, not the group standing `canManage` reads — a group admin
            or another group's operator on the project does not close it. */}
        {project.is_owner && project.status !== "archived" && (
          <>
            <Btn variant="secondary" onClick={() => setClosing("finish")}>Finish</Btn>
            <Btn variant="secondary" onClick={() => setClosing("cancel")}>Cancel project</Btn>
          </>
        )}
        {(project.tags || []).map(t => (
          <Badge key={t.id} color={t.colour || "blue"}>{t.name}</Badge>
        ))}
        {project.group_name && (
          <span style={{ fontSize: 12, padding: "2px 9px", borderRadius: 10, background: G_BG, color: "#006630", fontWeight: 500 }}>
            {project.group_name}
          </span>
        )}
        </div>
        {user?.is_admin && (
          <div data-page-actions style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <Btn variant="secondary" onClick={openDelete} style={{ color: "#b91c1c", borderColor: "#fca5a5" }}>
              Delete project
            </Btn>
          </div>
        )}
      </div>

      {/* A legacy project owned by somebody who is not an operator — logged at
          every boot. Not rewritten by any migration: a person decides who takes
          it over, so the page says who can (2026-11-03). */}
      {project.owner_is_operator === false && (
        <div role="alert" style={{ marginBottom: 20, padding: "14px 16px", borderRadius: 10, background: "rgba(217,119,6,0.10)", border: "1px solid rgba(217,119,6,0.45)", color: "#92400e", fontSize: 13 }}>
          <strong>This project's owner is not an operator.</strong> A project's owner is always one
          of the facility's operators. A group admin of {project.group_name || "its group"}, or an
          admin, transfers it to an operator under Members → Transfer ownership.
        </div>
      )}

      {/* The people who hit "upload failed" are the ones who need this, so it
          sits on the project page rather than only in the operator's log. */}
      {project.s3_health_ok === false && (
        <div role="alert" style={{ marginBottom: 20, padding: "14px 16px", borderRadius: 10, background: "rgba(217,119,6,0.10)", border: "1px solid rgba(217,119,6,0.45)", color: "#92400e", fontSize: 13, display: "flex", gap: 12 }}>
          <span aria-hidden="true" style={{ fontSize: 18 }}>⚠</span>
          {/* The fallback is load-bearing: every project row has a null kind
              until the next health check fills it in, and those keep exactly
              the wording they had before this lookup existed. */}
          {(() => {
            const look = HEALTH_LOOK[project.s3_health_kind] ?? HEALTH_LOOK.unreachable;
            return (
              <div>
                <strong>{look.headline}</strong> {look.body}
                {project.s3_health_error && (
                  <div style={{ marginTop: 4, fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>
                    {project.s3_health_error}
                  </div>
                )}
                {project.s3_health_checked_at && (
                  <div style={{ marginTop: 4, fontSize: 11 }}>
                    Last checked {formatWhen(project.s3_health_checked_at, { zone })}.
                    {canManage ? look.fix : " An operator has been notified."}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      <ProjectLogCard project={project} />

      {deleteInfo && (
        <ConfirmDialog
          title={`Delete “${project.name}” and everything it holds`}
          confirmWord={project.name}
          confirmLabel="Delete permanently"
          // Also disabled while the preview is loading: until it lands
          // `blockers` is empty, so the dialog would accept a confirmation for
          // something it is about to report as blocked.
          busy={deleting || !!deleteInfo.loading}
          blockers={deleteInfo.blockers || []}
          onConfirm={handleDelete}
          onClose={() => setDeleteInfo(null)}
        >
          {deleteInfo.loading ? (
            <div style={{ fontSize: 13, color: "#6b7280" }}><Spinner /> Checking what would be removed…</div>
          ) : (
            <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.6 }}>
              <p style={{ margin: "0 0 12px" }}>
                This removes the project's records <strong>and the research data in storage</strong>.
                It cannot be undone:
              </p>
              <ul style={{ margin: "0 0 12px", paddingLeft: 18 }}>
                <li>{deleteInfo.submissions} submission(s), {deleteInfo.results} delivery record(s), {deleteInfo.jobs} job(s)</li>
              </ul>
              {/* The figures are the safeguard. Somebody about to destroy data
                  should see how much of it there is, in this dialog, before
                  typing the name — not discover it afterwards in the log. */}
              <div style={{ padding: "10px 12px", borderRadius: 8, background: "#fef2f2", border: "1px solid #fecaca" }}>
                {deleteInfo.s3?.error ? (
                  <span style={{ color: "#92400e" }}>
                    <strong>What is in storage could not be listed</strong> ({deleteInfo.s3.error}).
                    The deletion will be refused rather than removing the records and leaving the
                    files behind with nothing recording where they are.
                  </span>
                ) : (
                  <>
                    <strong style={{ color: "#991b1b" }}>
                      {deleteInfo.s3?.truncated ? "At least " : ""}
                      {deleteInfo.s3?.object_count ?? 0} file(s)
                      {deleteInfo.s3?.total_bytes > 0 && <> ({fmtBytes(deleteInfo.s3.total_bytes)})</>}
                      {" "}will be permanently deleted
                    </strong>{" "}
                    from{" "}
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", wordBreak: "break-all" }}>
                      s3://{deleteInfo.s3?.bucket}/{deleteInfo.s3?.prefix}/
                    </span>
                    . There is no copy and no undo.
                  </>
                )}
              </div>
            </div>
          )}
        </ConfirmDialog>
      )}

      <div style={{ display: "flex", gap: 0, borderBottom: "2px solid #e5e7eb", marginBottom: 24 }}>
        {tabs.map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: "10px 20px", border: "none", background: "none", cursor: "pointer",
              fontSize: 14, fontWeight: tab === t ? 700 : 500, fontFamily: "inherit",
              color: tab === t ? G_DARK : "#6b7280",
              borderBottom: tab === t ? `2px solid ${G}` : "2px solid transparent",
              marginBottom: -2, transition: "all 0.15s",
            }}
          >
            {tabLabel[t]}
            {t === "submissions" && project.submission_count > 0 && (
              <span style={{ marginLeft: 6, background: G_BG, color: G_DARK, borderRadius: 10, padding: "1px 7px", fontSize: 11, fontWeight: 700 }}>
                {project.submission_count}
              </span>
            )}
            {t === "results" && project.result_count > 0 && (
              <span style={{ marginLeft: 6, background: G_BG, color: G_DARK, borderRadius: 10, padding: "1px 7px", fontSize: 11, fontWeight: 700 }}>
                {project.result_count}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === "overview"    && <OverviewTab project={project} isAdmin={user?.is_admin} canManageS3={canManage} onUpdated={setProject} />}
      {tab === "submissions" && <SubmissionsTab project={project} canManage={canManage} onProjectChanged={refreshProject} sessionHours={sessionHours} />}
      {tab === "results"     && <ResultsTab project={project} canManage={canManage} onProjectChanged={refreshProject} sessionHours={sessionHours} />}

      {reopening && (
        <ConfirmDialog
          title="Reopen this project?"
          confirmLabel="Reopen project"
          busyLabel="Reopening…"
          busy={reopening.busy}
          // Same shape as cancelling, and for the same reason: the person who
          // knows why is the one pressing the button, at the one moment they
          // still remember. ConfirmDialog locks its button while `blockers` is
          // non-empty, which is how the requirement shows on screen — the
          // server refuses an empty reason too, so this only saves a round trip.
          blockers={reopening.reason.trim() ? [] : ["A reason is required."]}
          onConfirm={async () => {
            const reason = reopening.reason.trim();
            if (!reason) return;
            setReopening(r => ({ ...r, busy: true }));
            try {
              setProject(await api.patch(`/api/projects/${id}`,
                                         { status: "active", reopen_reason: reason }));
              setReopening(null);
            } catch (err) {
              setError(err.message);
              setReopening(r => ({ ...r, busy: false }));
            }
          }}
          onClose={() => setReopening(null)}
        >
          <p style={{ margin: "0 0 16px", fontSize: 14, color: "#374151" }}>
            The project becomes active again and starts accepting data. What it was archived
            for is cleared from the row and kept in the log.
          </p>
          <Field
            label="Why"
            hint="Kept in the log, so the reason is still there when somebody asks later."
          >
            <Textarea
              rows={3}
              autoFocus
              value={reopening.reason}
              onChange={e => setReopening(r => ({ ...r, reason: e.target.value }))}
            />
          </Field>
        </ConfirmDialog>
      )}

      {closing && (
        <CloseProjectDialog
          projectId={id}
          mode={closing}
          onDone={updated => { setProject(updated); setClosing(null); }}
          onClose={() => setClosing(null)}
        />
      )}
    </PortalLayout>
  );
}
